/**
 * "앱 설치" — the one-tap install the download flow is really asking for.
 *
 * On mobile there is no downloadable installer file: iOS and Android do not let
 * a website hand over an executable. What they do allow — and what this uses —
 * is installing the app itself from the browser. So this is the honest shape of
 * "누르면 설치": where the browser supports it (Android, Chrome/Edge on any OS),
 * a tap installs the app and a home-screen/desktop icon appears; on iOS Safari,
 * which offers no install event, it shows the two-step "share → add to home
 * screen" instead of a dead button.
 *
 * When the app is already running installed (standalone), it renders nothing —
 * there is nothing left to install.
 */
import { useEffect, useState } from 'react';
import { Icon } from '@moduviewer/design/icon';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function InstallButton(): React.JSX.Element | null {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) { setInstalled(true); return; }

    const onPrompt = (event: Event): void => {
      // Keep the event so the install can happen on the user's click instead of
      // whenever the browser decided to offer it.
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    };
    const onInstalled = (): void => { setInstalled(true); setDeferred(null); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);

    // iOS Safari never fires `beforeinstallprompt`, so its users need the manual
    // route named for them rather than a button that would do nothing.
    const ua = navigator.userAgent;
    const isIOS = /iphone|ipad|ipod/i.test(ua)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPadOS
    const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios|chrome/i.test(ua);
    if (isIOS && isSafari) setIosHint(true);

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (installed) return null;

  if (deferred) {
    return (
      <button
        className="btn btn--primary install-btn"
        onClick={async () => {
          await deferred.prompt();
          await deferred.userChoice.catch(() => undefined);
          setDeferred(null);
        }}
      >
        <Icon name="download" size={16} />
        모두뷰어 설치
      </button>
    );
  }

  if (iosHint) {
    return (
      <p className="install-hint">
        <Icon name="download" size={15} />
        설치하려면 하단 <b>공유</b> 버튼 → <b>홈 화면에 추가</b>
      </p>
    );
  }

  return null;
}
